import type { Timestamp, Uuid } from './api';
import type { Message } from './message';

export const PROTOCOL_VERSION = 1;

/** Frames the client may send. */
export type ClientEventType =
  | 'heartbeat.ping'
  | 'session.resume'
  | 'message.send'
  | 'message.delivered'
  | 'message.read'
  | 'typing.start'
  | 'typing.stop'
  | 'call.offer'
  | 'call.accept'
  | 'call.answer'
  | 'call.ice_candidate'
  | 'call.reject'
  | 'call.end';

export interface ClientFrame<TPayload = Record<string, unknown>> {
  version: typeof PROTOCOL_VERSION;
  type: ClientEventType;
  request_id: Uuid;
  payload: TPayload;
}

/** Frames the server may send that answer a specific command. */
export interface CommandResponseFrame<TPayload = Record<string, unknown>> {
  version: typeof PROTOCOL_VERSION;
  type: string;
  request_id: Uuid;
  payload: TPayload;
}

/** Frames the server pushes without being asked. */
export interface ServerEventFrame<TPayload = Record<string, unknown>> {
  version: typeof PROTOCOL_VERSION;
  type: string;
  event_id: Uuid;
  /** Positive for durable per-device events, 0 for ephemeral ones. */
  sequence: number;
  occurred_at: Timestamp;
  payload: TPayload;
}

export interface ErrorFrame {
  version: typeof PROTOCOL_VERSION;
  type: 'error';
  request_id?: Uuid;
  payload: {
    code: string;
    message: string;
    retryable: boolean;
    details: Record<string, unknown>;
  };
}

export type ServerFrame = CommandResponseFrame | ServerEventFrame | ErrorFrame;

export interface SessionReadyPayload {
  device_id: Uuid;
  heartbeat_seconds: number;
  max_frame_bytes: number;
}

export interface MessageReceiptPayload {
  message_id: Uuid;
  chat_id: Uuid;
  device_id: Uuid;
  state: 'delivered' | 'read';
  occurred_at: Timestamp;
}

export interface MessageUpdatedPayload {
  message_id: Uuid;
  chat_id: Uuid;
  server_sequence: number;
  edited_at: Timestamp | null;
  content: string;
}

export interface MessageDeletedPayload {
  message_id: Uuid;
  chat_id: Uuid;
  server_sequence: number;
  deleted_at: Timestamp | null;
}

export interface TypingPayload {
  chat_id: Uuid;
  user_id: Uuid;
  typing: boolean;
  ttl_seconds: number;
}

export interface PresencePayload {
  user_id: Uuid;
  online: boolean;
}

/**
 * The events a client is expected to act on, mapped to their payloads.
 *
 * Unknown server event types are ignored rather than treated as errors, so a
 * newer server can add events without breaking an older client.
 */
export interface RealtimeEventMap {
  'session.ready': SessionReadyPayload;
  'message.created': Message;
  'message.updated': MessageUpdatedPayload;
  'message.deleted': MessageDeletedPayload;
  'message.receipt': MessageReceiptPayload;
  'typing.updated': TypingPayload;
  'presence.updated': PresencePayload;
  'chat.updated': { chat_id: Uuid };
  'group.updated': { chat_id: Uuid };
  'channel.updated': { chat_id: Uuid };
  'device.updated': { device_id: Uuid };
  'security.event': { code: string };
  'call.offer': Record<string, unknown>;
  'call.accept': Record<string, unknown>;
  'call.answer': Record<string, unknown>;
  'call.reject': Record<string, unknown>;
  'call.end': Record<string, unknown>;
  'call.taken': Record<string, unknown>;
  'call.ice_candidate': Record<string, unknown>;
}

export type RealtimeEventType = keyof RealtimeEventMap;

export type ConnectionState =
  | 'idle'
  | 'connecting'
  | 'authenticating'
  | 'connected'
  | 'reconnecting'
  | 'resync_required'
  | 'revoked'
  | 'disconnected'
  | 'unavailable';

export interface SyncEvent {
  event_id: Uuid;
  sequence: number;
  type: string;
  occurred_at: Timestamp;
  payload: Record<string, unknown>;
}

export interface SyncPage {
  events: SyncEvent[];
  cursor: number;
  has_more: boolean;
  resync_required: boolean;
}
